import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, MapPin, CheckCircle, Building2, Instagram, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaPosObraContagem = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Gostaria de solicitar um orçamento de limpeza pós-obra em Contagem.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/limpeza-pos-obra-contagem'].title}
        description={seoData['/limpeza-pos-obra-contagem'].description}
        canonical={seoData['/limpeza-pos-obra-contagem'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" alt="WellTec Logo" className="h-16 w-auto"/>
          </div>
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="text-green-400 font-semibold">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600"><MessageCircle className="w-4 h-4 mr-2"/>WhatsApp</Button>
          </div>
          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => {navigate('/'); setMobileMenuOpen(false);}} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button onClick={() => {navigate('/limpeza-pos-obra'); setMobileMenuOpen(false);}} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400">Limpeza Pós-Obra</button>
              <Button onClick={() => {handleWhatsAppClick(); setMobileMenuOpen(false);}} className="bg-green-500 w-full mt-2"><MessageCircle className="w-4 h-4 mr-2"/>WhatsApp</Button>
            </div>
          </div>
        )}
      </header>

      <section className="relative bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 text-white py-24 md:py-32"
        style={{backgroundImage: "linear-gradient(rgba(15,23,42,0.92), rgba(30,41,59,0.88)), url('https://images.unsplash.com/photo-1581244277943-fe4a9c777189?w=1600')", backgroundSize: 'cover'}}>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">CONTAGEM</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Limpeza Pós-Obra em Contagem</h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">Equipamentos profissionais para obras industriais, comerciais e residenciais</p>
            <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-500 hover:bg-green-600 px-8 py-6">
              <MessageCircle className="w-5 h-5 mr-2"/>Solicitar Orçamento em Contagem
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Limpeza Profissional Pós-Obra em Contagem</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Contagem possui forte atividade industrial e comercial, com constantes obras e reformas em galpões, fábricas e estabelecimentos. A WellTec oferece limpeza profissional pós-obra utilizando equipamentos industriais adequados para grandes áreas.
            </p>
            <Card className="bg-blue-50 border-2 border-blue-200 mt-8">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-8 h-8 text-blue-600 flex-shrink-0"/>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Atendimento em Contagem</h3>
                    <p className="text-gray-700">Industrial, Eldorado, Nacional, Cabral e todas as regiões de Contagem</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Especialidade em Áreas Industriais</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Galpões e Fábricas</h3>
                  <p className="text-gray-600">Limpeza de grandes áreas industriais após obras e adequações</p>
                </CardContent>
              </Card>
              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Áreas Comerciais</h3>
                  <p className="text-gray-600">Preparação de lojas e estabelecimentos para inauguração</p>
                </CardContent>
              </Card>
            </div>
            <div className="mt-10 text-center">
              <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-600 hover:bg-green-700 px-8 py-6">
                <MessageCircle className="w-5 h-5 mr-2"/>Solicitar Orçamento em Contagem
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400">&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
        </div>
      </footer>

      <button onClick={handleWhatsAppClick} className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50">
        <MessageCircle className="w-6 h-6"/>
      </button>
    </div>
  );
};

export default LimpezaPosObraContagem;
